import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import CustomModal from '../customModal';
import SelectField from '../selectField';
import VirtualizedList from '../virtualizedList';
import User from './user';
import http from '../../../utility/http';
import { getObjectToNumberArray, isArrayEmpty } from '../../../utility/helpers';
import { useAvailableUsers } from '../../../utility/hooks';
import { PARAMS } from '../../../constants/pages';
import { API_USERS_MULTIPLE } from '../../../constants/apiRoutes';
import CustomDatePicker from '../customDatePicker';
import { formatDate } from '../../../utility/dateUtils';
import { SINGLE_DAY_SAVE_FORMAT } from '../../../constants/date';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  description: {
    marginBottom: spacing(4),
  },
  usersViewport: {
    maxHeight: 188,
  },
  userWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  userLabel: {
    fontFamily: 'ProximaNova-Bold',
    fontSize: 16,
    lineHeight: '20px',
  },
  managersWrapper: {
    width: '100%',
    marginTop: spacing(4),
  },
  scrollY: {
    right: -19,
  },
  label: {
    marginTop: spacing(2),
  },
}));

const TransferEmployeesDialog = ({
  translations,
  isOpened,
  currentUserId = -1,
  organizationUser = {},
  minDate = undefined,
  users,
  onClose,
  onConfirm,
}) => {
  const classes = useStyles();
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [terminationDate, setTerminationDate] = useState(null);

  const params = useMemo(() => {
    if (!isOpened) return {};

    return currentUserId !== -1 && !isArrayEmpty(users)
      ? {
          [PARAMS.AVAILABLE_MANAGERS]: [
            currentUserId,
            ...getObjectToNumberArray(users),
          ],
        }
      : {};
  }, [isOpened, users, currentUserId]);

  const availableManagers = useAvailableUsers(params, organizationUser, true);

  const getVirtualUserSize = useCallback(
    index => (index === users.length - 1 ? 24 : 32),
    [users.length]
  );

  const handleSelect = managerId => setSelectedManagerId(managerId);

  const handleClose = useCallback(() => {
    setSelectedManagerId(null);
    setTerminationDate(null);
    onClose();
  }, [onClose]);

  const handleSave = async () => {
    const transferData = users.reduce(
      (acc, user) => [
        ...acc,
        {
          id: user.id,
          report_to: selectedManagerId,
        },
      ],
      []
    );
    await http.patch(API_USERS_MULTIPLE, transferData);
    await onConfirm(
      formatDate(terminationDate, SINGLE_DAY_SAVE_FORMAT),
      selectedManagerId
    );
    handleClose();
  };

  const renderUser = useCallback(user => <User user={user} />, []);

  return (
    <CustomModal
      title={translations.title}
      isOpened={isOpened}
      confirmButtonLabel={translations.save}
      closeButtonLabel={translations.cancel}
      isConfirmDisabled={!selectedManagerId || !terminationDate}
      onClose={handleClose}
      onConfirm={handleSave}
      isMedium
    >
      <div>
        <Typography className={classes.description}>
          {translations.description}
        </Typography>
        {!isArrayEmpty(users) && (
          <div>
            <VirtualizedList
              viewportClass={classes.usersViewport}
              verticalScrollClass={classes.scrollY}
              itemClass={classes.userWrapper}
              items={users}
              count={users.length}
              gap={8}
              estimateSize={getVirtualUserSize}
              renderItem={renderUser}
            />
          </div>
        )}
        <SelectField
          className={classes.managersWrapper}
          label={translations.label}
          placeholder={translations.placeholder}
          value={selectedManagerId}
          options={availableManagers}
          parser={{
            value: 'id',
            label: 'first_name',
          }}
          onChange={handleSelect}
          isUser
        />
        <CustomDatePicker
          labelClass={classes.label}
          label={translations.terminatedEmploymentLabel}
          placeholder={translations.terminatedEmploymentPlacegolder}
          value={terminationDate}
          disableToolbar
          minDate={minDate || undefined}
          onChange={value => setTerminationDate(value)}
          isIconOnStart
          fullWidth
          autoOk
        />
      </div>
    </CustomModal>
  );
};

TransferEmployeesDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentUserId: PropTypes.number,
  minDate: PropTypes.string,
  organizationUser: PropTypes.object,
  isOpened: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TransferEmployeesDialog;
